import type {
    AdIntelligenceRecordsSearchAdvertiserSpendDomainSeries,
    AdIntelligenceRecordsSearchAdvertiserSpendMonthlyRates,
    AdIntelligenceRecordsSearchAdvertiserSpendResponse,
} from "@similarweb/pro-api-client";

import type { DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { IMapSearchAdsSpendOptions, ISearchAdsSpend, ISearchAdsSpendPoint, ISearchAdsSpendSeries } from "./types";

export const USD_CURRENCY = "USD";

const DISPLAY_DECIMALS = 2;
const USD_RATE = 1;

const webSourceToSeriesKey = {
    [devicesTypes.DESKTOP]: "desktop",
    [devicesTypes.MOBILE]: "mobileWeb",
    [devicesTypes.TOTAL]: "total",
} as const satisfies Record<DeviceType, keyof AdIntelligenceRecordsSearchAdvertiserSpendDomainSeries>;

/** Values arrive unrounded; the widget endpoints this replaces emitted two decimals. */
const roundForDisplay = (value: number): number => {
    const factor = 10 ** DISPLAY_DECIMALS;
    return Math.round(value * factor) / factor;
};

const getRate = (
    exchangeRates: Record<string, AdIntelligenceRecordsSearchAdvertiserSpendMonthlyRates> | undefined,
    month: string,
    currency: string,
): number | undefined => {
    if (currency === USD_CURRENCY) {
        return USD_RATE;
    }
    return exchangeRates?.[month]?.unitsPerUsd?.[currency];
};

const sumPoints = (points: ISearchAdsSpendPoint[]): number | null => {
    const values = points.filter((point) => point.value !== null);
    return values.length > 0 ? values.reduce((sum, point) => sum + point.value, 0) : null;
};

/**
 * Converts the USD series into the requested currency.
 *
 * Rates are retained for a rolling ~3 year window while spend goes back to 2020-01, so a month can
 * carry spend that no rate covers. Such a month is reported through `isRateMissing` instead of being
 * multiplied by a stale rate.
 */
export const mapSearchAdsSpend = (
    response: AdIntelligenceRecordsSearchAdvertiserSpendResponse | undefined,
    { currency, webSource }: IMapSearchAdsSpendOptions,
): ISearchAdsSpend => {
    const seriesKey = webSourceToSeriesKey[webSource] ?? webSourceToSeriesKey[devicesTypes.TOTAL];
    const monthsWithoutRates = new Set<string>();

    const series: ISearchAdsSpendSeries[] = Object.entries(response?.domains ?? {}).map(([domain, domainSeries]) => {
        const points = Object.entries(domainSeries?.[seriesKey]?.usdSpend ?? {}).map(
            ([date, usdSpend]): ISearchAdsSpendPoint => {
                if (usdSpend === null) {
                    return { date, value: null, isRateMissing: false };
                }

                const rate = getRate(response?.exchangeRates, date, currency);
                if (rate === undefined) {
                    monthsWithoutRates.add(date);
                    return { date, value: null, isRateMissing: true };
                }

                return { date, value: roundForDisplay(usdSpend * rate), isRateMissing: false };
            },
        );

        return { domain, points, total: sumPoints(points) };
    });

    return { series, monthsWithoutRates: [...monthsWithoutRates].sort() };
};

export const hasSearchAdsSpendData = (spend: ISearchAdsSpend): boolean =>
    spend.series.some((series) => series.total !== null);
